import Button from '@mui/material/Button'
import DialogContent from '@mui/material/DialogContent'
import React, { useCallback, useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { useCreateCSNoteMutation, useUpdateCSNoteMutation } from 'api/notes/configuredServiceNotesCrud'
import CancelButton from 'components/buttons/CancelButton'
import HHBaseDialog from 'components/common/HHBaseDialog'
import HHDialogActions from 'components/common/HHDialogActions'
import HHDialogTitle from 'components/common/HHDialogTitle'
import { HHFormTextField } from 'components/form-fields/v5'
import T from 'T'
import { handleError } from 'utils/error'
import { isEnterOrNumpadEnterWithCtrlOrMeta } from 'utils/keyboard'
import { get } from 'utils/lodash'

const HHBaseDialogUntyped = HHBaseDialog as React.ElementType
const CancelButtonUntyped = CancelButton as React.ElementType
const HHFormTextFieldUntyped = HHFormTextField as React.ElementType

type NoteFormValues = { note: string }
type ExistingNote = { id?: string; note?: string }

type AddUpdateCSNoteDialogProps = {
  id: string
  isOpen?: boolean
  title?: string
  existingNote?: ExistingNote
  onClose: () => void
}

const noteFieldSx = { mt: 3, '& .MuiOutlinedInput-root': { height: 'inherit' } }

const AddUpdateCSNoteDialog = ({ id, isOpen = false, title = T.NEW_NOTE, existingNote = {}, onClose }: AddUpdateCSNoteDialogProps) => {
  const [createCSNote, { isLoading: isCreateCSNoteLoading }] = useCreateCSNoteMutation()
  const [updateCSNote, { isLoading: isUpdateCSNoteLoading }] = useUpdateCSNoteMutation()
  const CSNoteForm = useForm<NoteFormValues>({ defaultValues: { note: '' } })
  const {
    control,
    reset,
    handleSubmit,
    formState: { isDirty, errors },
  } = CSNoteForm

  const noteHelperText = get(errors, 'note.message')
  const noteId = get(existingNote, 'id')

  const updateNote = useCallback(
    (note: string) =>
      updateCSNote({ id, noteId, data: { note, updateInherited: false } })
        .unwrap()
        .then(() => {
          toast.success('Note updated successfully')
          onClose()
        })
        .catch(handleError),
    [id, noteId, onClose, updateCSNote]
  )

  const handleSave = useCallback(
    (data: NoteFormValues) => {
      if (!isDirty) {
        onClose()
        return
      }

      if (!noteId) {
        // New note
        createCSNote({ id, data: { note: data.note, inherit: false } })
          .unwrap()
          .then(() => {
            toast.success('Note added successfully')
            onClose()
          })
          .catch(handleError)
        return
      }

      updateNote(data.note)
    },
    [createCSNote, id, isDirty, noteId, onClose, updateNote]
  )

  const handleCSNoteKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (isEnterOrNumpadEnterWithCtrlOrMeta(event)) {
        handleSubmit(handleSave)()
      }
    },
    [handleSave, handleSubmit]
  )

  const handleSaveClick = useCallback(() => handleSubmit(handleSave)(), [handleSave, handleSubmit])

  // Seeds the form when the dialog opens, and only then: `existingNote` defaults to a fresh
  // object literal each render, so listing it here would re-seed on every render and discard
  // whatever the user had typed.
  useEffect(() => {
    if (isOpen) {
      reset({ note: get(existingNote, 'note', '') })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  return (
    <HHBaseDialogUntyped fullWidth maxWidth="sm" open={isOpen} onClose={onClose}>
      <HHDialogTitle title={title} onClose={onClose} />
      <DialogContent>
        <FormProvider {...CSNoteForm}>
          <HHFormTextFieldUntyped
            autoFocus
            fullWidth
            multiline
            control={control}
            deprecatedLabel={false}
            error={noteHelperText}
            helperText={noteHelperText}
            label="Note"
            minRows={4}
            name="note"
            placeholder="Leave a note"
            rules={{ required: T.CANNOT_BE_EMPTY }}
            sx={noteFieldSx}
            onKeyDown={handleCSNoteKeyDown}
          />
        </FormProvider>
      </DialogContent>
      <HHDialogActions>
        <CancelButtonUntyped onClick={onClose} />
        <Button
          color="primary"
          loading={isCreateCSNoteLoading || isUpdateCSNoteLoading}
          size="small"
          variant="contained"
          onClick={handleSaveClick}
        >
          {T.SAVE}
        </Button>
      </HHDialogActions>
    </HHBaseDialogUntyped>
  )
}

export default AddUpdateCSNoteDialog
